import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { trpc } from '../../../trpc/client';
import { useForm } from 'react-hook-form';
import { ArrowLeft, Save, Loader2, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

// Type for form data
interface BlogFormData {
    title: string;
    slug: string;
    excerpt?: string;
    content: string;
    coverImage?: string;
    published: boolean;
    tags?: string; // Comma separated string for input
}

const AdminBlogEditorPage = () => {
    const { id } = useParams<{ id: string }>(); // If id present, we are editing
    const navigate = useNavigate();
    const isEditMode = !!id;

    const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<BlogFormData>();

    // Auto-generate slug from title if creating
    const titleValue = watch('title');
    useEffect(() => {
        if (!isEditMode && titleValue) {
            const slug = titleValue.toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/(^-|-$)+/g, '');
            setValue('slug', slug);
        }
    }, [titleValue, isEditMode, setValue]);

    // Fetch post if editing
    const { data: post, isLoading: isLoadingPost } = trpc.blog.getPostBySlug.useQuery(
        { slug: '' }, // We don't have slug yet, wait. Actually we need getPostById but we only exposed getBySlug.
        // Let's implement fetch by slug or modify router. 
        // For now, let's misuse "getAllPosts" to filter client side or fix router.
        // Better yet, let's assume we can fetch by slug if we had it, but we have ID.
        // To avoid modifying backend again right now, I'll allow "slug" in query to be skipped and I will fetch it if I can...
        // Actually I implemented `getAllPosts` which is for list... 
        // I realized I didn't verify `start` logic for `getPostById` for admin.
        // Robust Fix: Just rely on `getAllPosts` searching by ID? No, that's messy.
        // Let's rely on the public `getPostBySlug` BUT we need the slug. 
        // Since this page handles "create" (no ID) and "edit" (has ID), for "edit" usually we arrive from list which has slug.
        // But wait, the list links to `/edit/:id`. I should link to `/edit/:slug` or fetch ID.
        // I'll assume for now I can pass `slug` in URL if I want, OR I will just use `getPostBySlug` logic...
        // Actually, I should update the backend to support `getPostById` for admin.
        // BUT, to satisfy "Robustness" without breaking flow, I'll update the previous List page to link to `/edit/:slug`? No duplicate slugs allowed so it's unique.
        // Ok, I will Modify `AdminBlogPage` link to use `slug` instead of `id` for editing?
        // Or I will modify this page to accept `slug` in params instead of ID.
        // Let's use `slug` in URL for editing. It's cleaner.
        // SO: URL will be `/admin/blog/edit/:slug`.
        { enabled: false }
    );

    // Actually, let's use the trpc utils to get data from cache if possible, or fetch.
    // I'll make a quick fix to the Router later if needed, but for now let's restart:
    // I will make the router support `getPostById`.
    // Wait, I can't easily jump back to backend task. 
    // I will update the frontend route to be `/admin/blog/edit/:slug` in the Route definition.
    // And the list page to link there. 
    // Then I can use `getPostBySlug`.

    const { slug } = useParams<{ slug: string }>(); // Use slug instead of ID
    const { data: existingPost, isLoading: isLoadingData } = trpc.blog.getPostBySlug.useQuery(
        { slug: slug! },
        { enabled: !!slug }
    );

    useEffect(() => {
        if (existingPost) {
            setValue('title', existingPost.title);
            setValue('slug', existingPost.slug);
            setValue('content', existingPost.content);
            setValue('excerpt', existingPost.excerpt || '');
            setValue('coverImage', existingPost.coverImage || '');
            setValue('published', existingPost.published);
            setValue('tags', existingPost.tags.join(', '));
        }
    }, [existingPost, setValue]);

    const createMutation = trpc.blog.createPost.useMutation({
        onSuccess: () => {
            toast.success('Post created successfully!');
            navigate('/admin/blog');
        },
        onError: (err: any) => toast.error(err.message)
    });

    const updateMutation = trpc.blog.updatePost.useMutation({
        onSuccess: () => {
            toast.success('Post updated successfully!');
            navigate('/admin/blog');
        },
        onError: (err: any) => toast.error(err.message)
    });

    const onSubmit = async (data: BlogFormData) => {
        const tagsArray = data.tags ? data.tags.split(',').map(t => t.trim()).filter(Boolean) : [];

        try {
            if (slug && existingPost) {
                await updateMutation.mutateAsync({
                    id: existingPost.id, // We need ID for update
                    ...data,
                    tags: tagsArray
                });
            } else {
                await createMutation.mutateAsync({
                    ...data,
                    tags: tagsArray
                });
            }
        } catch (e) { }
    };

    if (slug && isLoadingData) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin" /></div>;

    return (
        <div className="max-w-4xl mx-auto pb-20">
            <header className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate('/admin/blog')} className="p-2 hover:bg-muted rounded-full transition-colors">
                        <ArrowLeft className="w-5 h-5 text-muted-foreground" />
                    </button>
                    <h1 className="text-3xl font-black text-foreground tracking-tight">
                        {slug ? 'Edit Post' : 'Create New Post'}
                    </h1>
                </div>
                <button
                    onClick={handleSubmit(onSubmit)}
                    disabled={createMutation.isLoading || updateMutation.isLoading}
                    className="bg-primary text-primary-foreground px-8 py-3 rounded-xl font-bold hover:bg-primary/90 flex items-center gap-2 disabled:opacity-50 transition-all shadow-lg shadow-primary/20"
                >
                    {(createMutation.isLoading || updateMutation.isLoading) ? <Loader2 className="animate-spin w-4 h-4" /> : <Save className="w-4 h-4" />}
                    Save Post
                </button>
            </header>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
                {/* Main Card */}
                <div className="bg-card p-8 rounded-2xl shadow-sm border border-border space-y-8">

                    {/* Title */}
                    <div>
                        <label className="block text-sm font-bold text-foreground mb-2">Article Title</label>
                        <input
                            {...register('title', { required: 'Title is required' })}
                            className="w-full text-xl md:text-2xl font-bold px-4 py-3 bg-background border border-border rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none text-foreground placeholder:text-muted-foreground transition-all"
                            placeholder="Enter a catchy title..."
                        />
                        {errors.title && <p className="text-destructive text-sm mt-1">{errors.title.message}</p>}
                    </div>

                    {/* Slug */}
                    <div>
                        <label className="block text-sm font-bold text-foreground mb-2">URL Slug</label>
                        <div className="flex items-center">
                            <span className="bg-muted border border-r-0 border-border px-4 py-3 rounded-l-xl text-muted-foreground text-sm font-mono">/blog/</span>
                            <input
                                {...register('slug', { required: 'Slug is required', pattern: { value: /^[a-z0-9-]+$/, message: 'Lowercase alphanumeric and dashes only' } })}
                                className="flex-grow px-4 py-3 bg-background border border-border rounded-r-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none font-mono text-sm text-foreground transition-all"
                            />
                        </div>
                        {errors.slug && <p className="text-destructive text-sm mt-1">{errors.slug.message}</p>}
                    </div>

                    {/* Excerpt */}
                    <div>
                        <label className="block text-sm font-bold text-foreground mb-2">Excerpt (Optional)</label>
                        <textarea
                            {...register('excerpt')}
                            rows={3}
                            className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none text-foreground placeholder:text-muted-foreground resize-none transition-all"
                            placeholder="Short summary for SEO and previews..."
                        />
                    </div>

                    {/* Tags */}
                    <div>
                        <label className="block text-sm font-bold text-foreground mb-2">Tags (Comma separated)</label>
                        <input
                            {...register('tags')}
                            className="w-full px-4 py-3 bg-background border border-border rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none text-foreground placeholder:text-muted-foreground transition-all"
                            placeholder="e.g. Logistics, Technology, Updates"
                        />
                    </div>

                    {/* Cover Image URL */}
                    <div>
                        <label className="block text-sm font-bold text-foreground mb-2">Cover Image URL</label>
                        <div className="flex gap-2">
                            <div className="relative flex-grow">
                                <ImageIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                                <input
                                    {...register('coverImage')}
                                    className="w-full pl-11 pr-4 py-3 bg-background border border-border rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none text-foreground placeholder:text-muted-foreground transition-all"
                                    placeholder="https://..."
                                />
                            </div>
                        </div>
                        {watch('coverImage') && (
                            <div className="mt-4 h-64 w-full rounded-xl overflow-hidden bg-muted border border-border">
                                <img src={watch('coverImage')} alt="Preview" className="w-full h-full object-cover" />
                            </div>
                        )}
                    </div>
                </div>

                {/* Content Editor */}
                <div className="bg-card p-8 rounded-2xl shadow-sm border border-border">
                    <label className="block text-sm font-bold text-foreground mb-4">Content (Markdown)</label>
                    <textarea
                        {...register('content', { required: 'Content is required' })}
                        rows={20}
                        className="w-full px-6 py-6 bg-background border border-border rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none font-mono text-sm leading-relaxed text-foreground placeholder:text-muted-foreground transition-all"
                        placeholder="# Your Article Title\n\nWrite your content here..."
                    />
                    {errors.content && <p className="text-destructive text-sm mt-1">{errors.content.message}</p>}
                </div>

                {/* Settings */}
                <div className="bg-card p-8 rounded-2xl shadow-sm border border-border">
                    <h3 className="text-lg font-bold text-foreground mb-4">Publishing Settings</h3>
                    <label className="flex items-center gap-4 cursor-pointer p-4 border border-border rounded-xl hover:bg-muted/30 transition-colors">
                        <input
                            type="checkbox"
                            {...register('published')}
                            className="w-6 h-6 text-primary rounded-md focus:ring-primary border-gray-300"
                        />
                        <div>
                            <span className="block font-bold text-foreground">Publish immediately</span>
                            <span className="block text-sm text-muted-foreground">Uncheck to save as Draft</span>
                        </div>
                    </label>
                </div>

            </form>
        </div>
    );
};

export default AdminBlogEditorPage;
